/* ==========================================================================
   OnCE — entry point

   Boot order matters in three places:
     1. The drop state resolves first: everything commercial asks it what
        world it is in before rendering.
     2. Drop 001 renders before the reveal observers attach, so the product
        cards are observed like everything else.
     3. The scroll bus starts before anything subscribes to it.

   Everything else is independent, and a failure in any one module is caught
   and logged rather than taking the page down. On a storefront a broken
   animation must never cost a sale.
   ========================================================================== */

import { CONFIG } from './once-settings.js';
import { canAnimate, qs } from './once-dom.js';
import { initScroll, refresh } from './once-scroll.js';
import { initReveal } from './once-reveal.js';
import { createDropState } from './once-drop-state.js';

import { initIntro } from './once-intro.js';
import { initNav } from './once-nav.js';
import { initCursor } from './once-cursor.js';
import { initClock } from './once-clock.js';
import { initCart } from './once-cart.js';
import { initNewsletter } from './once-newsletter.js';
import { initCommerceGate } from './once-commerce-gate.js';

import { initHero } from './once-hero.js';
import { initNarrative } from './once-narrative.js';
import { initPhilosophy } from './once-philosophy.js';
import { initDaily } from './once-daily.js';
import { initCountdown } from './once-countdown.js';
import { initQuarantine } from './once-quarantine.js';
import { initDrop } from './once-drop.js';
import { initDetails } from './once-details.js';
import { initProductDescription } from './once-product-description.js';
import { initProductGallery } from './once-product-gallery.js';

import { createCartApi } from './once-cart-api.js';

const safely = (name, fn) => {
  try { return fn(); } catch (err) { console.error(`[once] ${name}`, err); return null; }
};

function boot() {
  const { options, drop, daily, email, products } = CONFIG;
  const currency = CONFIG.currency || products[0]?.currency || 'USD';

  initScroll();

  /* ---- State ----------------------------------------------------------- */
  const dropState = safely('dropState', () => createDropState(drop));

  /* ---- Commerce first --------------------------------------------------
   * If every other module failed, a visitor could still choose a size and
   * check out.                                                           */
  const api = createCartApi(CONFIG);
  const cart = safely('cart', () => initCart(api, { currency }));
  safely('drop', () => initDrop(products, cart, currency));
  if (dropState) {
    safely('countdown', () => initCountdown(dropState, drop));
    safely('commerceGate', () => initCommerceGate(dropState));
  }
  safely('newsletter', () => initNewsletter(email));

  /* ---- Chrome ---------------------------------------------------------- */
  safely('nav', () => initNav());
  safely('clock', () => initClock());
  safely('cursor', () => initCursor(options.customCursor));

  /* ---- Content --------------------------------------------------------- */
  safely('daily', () => initDaily({
    ...daily,
    timeZone: daily.timeZone || dropState?.timeZone || drop.timeZone,
  }));

  /* ---- Scroll behaviour ------------------------------------------------ */
  safely('hero', () => {
    const launchDate = new Date(`${drop.openDate}T12:00:00Z`);
    let dropLabel = drop.name;
    if (Number.isFinite(launchDate.getTime())) {
      dropLabel = `${drop.name} — ${new Intl.DateTimeFormat('en-US', {
        month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
      }).format(launchDate)}`;
    }
    return initHero({ dropLabel });
  });
  safely('narrative', () => initNarrative());
  safely('philosophy', () => initPhilosophy());
  safely('quarantine', () => initQuarantine());
  safely('productDescription', () => initProductDescription());
  safely('productGallery', () => initProductGallery());
  safely('details', () => initDetails());
  safely('reveal', () => initReveal());

  refresh();

  /* ---- Intro -----------------------------------------------------------
   * Last, and awaited by nothing. The page underneath is fully built and
   * interactive before the curtain lifts, so skipping the intro lands on a
   * finished hero rather than a loading one.                             */
  safely('intro', () => initIntro({
    duration: options.introDuration,
    oncePerSession: options.introOncePerSession,
  })?.then(refresh));

  document.documentElement.classList.add('is-ready');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

/* In-page anchors.

   The handler has to match links written as `/#drop` as well as `#drop`. The
   Shopify layout builds its menu from `routes.root_url`, so every nav item
   arrives as `/#drop` — which the old `a[href^="#"]` test missed, and the
   browser treated as a navigation. Tapping "Drop 001" reloaded the whole page
   instead of scrolling, which is why the menu appeared to lead nowhere.

   Native smooth scrolling does the easing; scroll-padding-top in base.css and
   scroll-margin-top on each section keep the fixed header off the content. */
document.addEventListener('click', (e) => {
  const link = e.target.closest?.('a[href]');
  if (!link || e.defaultPrevented || e.button !== 0 || link.target === '_blank'
      || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

  const href = link.getAttribute('href');
  if (!href || !href.includes('#')) return;

  let url;
  try { url = new URL(href, location.href); } catch { return; }

  // Only intercept links pointing at a fragment on this very page.
  if (url.origin !== location.origin || url.pathname !== location.pathname) return;
  if (!url.hash || url.hash === '#') return;

  let id;
  try { id = decodeURIComponent(url.hash.slice(1)); } catch { return; }
  const target = document.getElementById(id);
  if (!target) return;

  e.preventDefault();

  /* Hold the header in place for the duration of the jump. Without this the
     downward scroll stows it, and the menu button someone just used vanishes
     until they scroll back up. */
  const root = document.documentElement;
  root.setAttribute('data-anchor-scroll', '');
  clearTimeout(window.__onceAnchorTimer);
  window.__onceAnchorTimer = setTimeout(() => root.removeAttribute('data-anchor-scroll'), 1200);

  target.scrollIntoView({ behavior: canAnimate() ? 'smooth' : 'auto', block: 'start' });
  history.pushState(null, '', url.hash);

  // Move focus so keyboard and screen-reader users arrive where sighted users
  // do, without the outline flashing on a mouse click.
  target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
});
